import {
  apiServer,
  onBoardingAlbumIdForDesktop,
  onBoardingAlbumIdDarkmodeForDesktop,
} from "../config";

export const ALBUM_TYPE_ONBOARDING = "onboarding";
export const ALBUM_TYPE_HOME = "home";
export const ALBUM_TYPE_HOW_TO = "how to";

export type AlbumType =
  | typeof ALBUM_TYPE_ONBOARDING
  | typeof ALBUM_TYPE_HOME
  | typeof ALBUM_TYPE_HOW_TO;

export const ALBUM_APPEARANCE_HORIZONTAL_SLIDER = "Horizontal Slider";
export const ALBUM_APPEARANCE_VERTICAL_LIST = "Vertical List";

export type AlbumAppearance =
  | typeof ALBUM_APPEARANCE_HORIZONTAL_SLIDER
  | typeof ALBUM_APPEARANCE_VERTICAL_LIST;

export const ALBUM_COLOR_MODE_DARK = "Dark Mode";
export const ALBUM_COLOR_MODE_LIGHT = "Light Mode";

export type AlbumColorMode =
  | typeof ALBUM_COLOR_MODE_DARK
  | typeof ALBUM_COLOR_MODE_LIGHT;

export type AlbumDevice = "mobile" | "desktop";

export interface Photo {
  id: number;
  album_id: number;
  image: string;
  /** The album's own icon for the row — already tinted and padded by the CMS. */
  icon_image?: string | null;
  /** Absolute URLs the API resolves for `image` / `icon_image`. */
  image_url?: string | null;
  icon_image_url?: string | null;
  alt_text: string;
  position: number;
  created_at: string;
  updated_at: string;
  title: string;
  description?: string;
  content?: string | null;
}

export interface Album {
  id: number;
  name: string;
  speed: number | null;
  delay: number | null;
  album_type: AlbumType | string;
  album_device: AlbumDevice | null;
  color_mode: AlbumColorMode | string;
  appearance: AlbumAppearance | string;
  /** Section heading, only set on vertical-list albums. */
  heading: string | null;
  active: number;
  created_at: string;
  updated_at: string;
  photos: Photo[];
}

/** The album shape under its previous name. */
export type OnboardingPhotos = Album;

export interface AlbumsByType {
  [ALBUM_TYPE_ONBOARDING]: Album[];
  [ALBUM_TYPE_HOME]: Album[];
  [ALBUM_TYPE_HOW_TO]: Album[];
  [albumType: string]: Album[];
}

export interface OnboardingResult {
  status: string;
  message: string;
  data: AlbumsByType;
}

interface AlbumLoadResponse {
  status: string;
  message: string;
  data: AlbumsByType | Album[] | null;
}

function emptyAlbumsByType(): AlbumsByType {
  return {
    [ALBUM_TYPE_ONBOARDING]: [],
    [ALBUM_TYPE_HOME]: [],
    [ALBUM_TYPE_HOW_TO]: [],
  };
}

/**
 * Normalise either album payload into groups: /album/load answers with a flat
 * list, the device-scoped routes with an object already keyed by album_type.
 */
function groupAlbums(data: AlbumLoadResponse["data"]): AlbumsByType {
  const grouped = emptyAlbumsByType();

  if (Array.isArray(data)) {
    data.forEach((album) => {
      const albumType = album?.album_type || ALBUM_TYPE_ONBOARDING;
      grouped[albumType] = [...(grouped[albumType] ?? []), album];
    });

    return grouped;
  }

  Object.entries(data ?? {}).forEach(([albumType, albums]) => {
    grouped[albumType] = Array.isArray(albums) ? albums : [];
  });

  return grouped;
}

/**
 * Active albums keyed by album_type.
 *
 * Unscoped reads /album/load, which stays a flat list for shipped app builds and
 * is grouped here instead; the grouping is server-side on the device routes.
 *
 * @param albumDevice scopes to one device; albums with no device set are then excluded
 */
export async function loadAlbums(
  albumDevice?: AlbumDevice,
): Promise<AlbumsByType> {
  const path = albumDevice ? `/album/load/${albumDevice}` : "/album/load";
  const response = await fetch(apiServer + path);
  const result: AlbumLoadResponse = await response.json();

  if (result.status === "error") {
    throw new Error(result.message);
  }

  return groupAlbums(result.data);
}

export function albumsOfType(
  albums: AlbumsByType | undefined,
  albumType: string,
): Album[] {
  return albums?.[albumType] ?? [];
}

export function albumMatchesColorMode(album: Album, isDark: boolean): boolean {
  const colorMode = album.color_mode || ALBUM_COLOR_MODE_LIGHT;

  return isDark
    ? colorMode === ALBUM_COLOR_MODE_DARK
    : colorMode !== ALBUM_COLOR_MODE_DARK;
}

export function isVerticalListAlbum(album?: Album | null): boolean {
  return album?.appearance === ALBUM_APPEARANCE_VERTICAL_LIST;
}

/** Every album, whatever its type. */
function allAlbums(albums: AlbumsByType): Album[] {
  return ([] as Album[]).concat(...Object.values(albums));
}

/** An album's name without its colour-mode suffix, trimmed and lower-cased. */
function albumBaseName(name: string): string {
  return name
    .trim()
    .replace(/\s*\((light|dark)\)$/i, "")
    .toLowerCase();
}

export async function loadOnboardingData(
  isMobile: boolean,
  isDark: boolean,
  /**
   * Pick one specific album by id instead of inferring it from the platform.
   * Albums other than the onboarding pair (e.g. the home slider) are addressed
   * this way, because the platform fallbacks below only look at onboarding.
   */
  albumId?: number | string,
  /**
   * Pick an album by its CMS `name` — e.g. "Contribute/Setup profile".
   *
   * The alternative to hardcoding another id: an album that is created in the
   * CMS after the app ships has an id nobody can know at build time, but its
   * name is agreed up front. Matched case-insensitively and trimmed, because
   * the name is typed into the CMS by hand. Takes precedence over `albumId`.
   */
  albumName?: string,
) {
  // Mobile stays on the unscoped route, the one native has always called: a shipped
  // build reaching users before the API deploy still gets a body, not a 404.
  const albums = await loadAlbums(isMobile ? undefined : "desktop");

  // An album addressed directly can be of any type — a how-to lesson, the home
  // slider — so it is looked for across every group, not only onboarding.
  // The CMS keeps one album per colour mode, told apart by a " (Light)" /
  // " (Dark)" suffix on the name, so the suffix is ignored here and the album's
  // `color_mode` picks between the pair.
  if (albumName !== undefined) {
    const wanted = albumBaseName(albumName);
    const named = allAlbums(albums).filter(
      (album) => albumBaseName(album.name ?? "") === wanted,
    );
    return (
      named.find((album) => albumMatchesColorMode(album, isDark)) ?? named[0]
    );
  }

  if (albumId !== undefined) {
    return allAlbums(albums).find(
      (album) => album.id.toString() === albumId.toString(),
    );
  }

  const onboarding = albumsOfType(albums, ALBUM_TYPE_ONBOARDING);

  if (isMobile) {
    // Dark and light mobile onboarding are separate albums of baked images,
    // so the colour mode picks the set rather than restyling one.
    return (
      onboarding.find(
        (album) =>
          album.album_device === "mobile" &&
          albumMatchesColorMode(album, isDark),
      ) ?? onboarding[0]
    );
  }

  const pinnedAlbumId =
    isDark && onBoardingAlbumIdDarkmodeForDesktop
      ? onBoardingAlbumIdDarkmodeForDesktop
      : onBoardingAlbumIdForDesktop;

  if (pinnedAlbumId) {
    const pinned = onboarding.find(
      (album) => album.id.toString() === pinnedAlbumId.toString(),
    );

    if (pinned) return pinned;
  }

  // The route already scoped this to desktop, so colour mode is all that is left to match.
  return (
    onboarding.find((album) => albumMatchesColorMode(album, isDark)) ??
    onboarding[0]
  );
}
